// src/components/Auth/AuthPanel.tsx
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence, LayoutGroup } from 'framer-motion';
import { IntroPage } from './IntroPage';
import { RequestSuccessPage } from './RequestSuccessPage';
import { InviteModal } from './InviteModal';
import { LoginModal } from './LoginModal';
import { SignupModal } from './SignupModal';
import { WelcomeBackScreen } from './WelcomeBackScreen';
import { SignupSuccessScreen } from './SignupSuccessScreen';
import { AuthSuccessScreen } from './AuthSuccessScreen';
import { useMeasureHeight } from '../../hooks/useMeasureHeight';
import { AuthStep } from './AuthFlowController'; // Import only the type
import { authService } from '../../services/AuthService'; // Add this import


interface AuthPanelProps {
  isAuthenticated: boolean;
  //onAuthSuccess: () => void;
  onAuthSuccess: (sessionToken: any) => void;
}

// Animation variants for sliding in from right
/* const panelVariants = {
  hidden: { 
    x: '100%', 
    opacity: 0,
    transition: {
      type: 'spring',
      stiffness: 400,
      damping: 40,
      duration: 0.3
    }
  },
  visible: { 
    x: 0, 
    opacity: 1,
    transition: { 
      type: 'spring', 
      stiffness: 300, 
      damping: 26,
      duration: 0.4
    } 
  },
  exit: { 
    x: '100%', 
    opacity: 0,
    transition: { 
      type: 'spring',
      stiffness: 400,
      damping: 38,
      duration: 0.3
    } 
  }
};

// Animation variants for step transitions
const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? '100%' : '-100%',
    opacity: 0
  }),
  center: {
    x: 0,
    opacity: 1
  },
  exit: (direction: number) => ({
    x: direction < 0 ? '100%' : '-100%',
    opacity: 0
  })
}; */

// Animation variants for sliding in from right
const panelVariants = {
  hidden: { 
    x: '100%', 
    opacity: 1,  // Changed from 0 to 1 to avoid opacity animation
    transition: {
      type: 'spring',
      stiffness: 400,
      damping: 40,
      duration: 0.3
    }
  },
  visible: { 
    x: 0, 
    opacity: 1,
    transition: { 
      type: 'spring', 
      stiffness: 300, 
      damping: 26,
      duration: 0.4
    } 
  },
  exit: { 
    x: '100%', 
    opacity: 1,  // Changed from 0 to 1 to avoid opacity animation
    transition: { 
      type: 'spring',
      stiffness: 400,
      damping: 38,
      duration: 0.3
    } 
  }
};

// Animation variants for step transitions - simplified to avoid interfering with panel animation
const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? '50px' : '-50px',  // Reduced the distance
    opacity: 0
  }),
  center: {
    x: 0,
    opacity: 1,
    transition: {
      duration: 0.2 // Shorter duration
    }
  },
  exit: (direction: number) => ({
    x: direction < 0 ? '50px' : '-50px',  // Reduced the distance
    opacity: 0,
    transition: {
      duration: 0.1 // Even shorter exit duration
    }
  })
};


export function AuthPanel({ isAuthenticated, onAuthSuccess }: AuthPanelProps) {
  // State to track the current step in the flow
  const [currentStep, setCurrentStep] = useState<AuthStep>('intro');
  
  // Track previous step to determine animation direction
  const [previousStep, setPreviousStep] = useState<AuthStep | null>(null);

  const [lockToLoginScreen, setLockToLoginScreen] = useState(false);

  
  // State to store user data between steps
  const [userData, setUserData] = useState<{
    email?: string;
    name?: string;
    isWaitlisted?: boolean;
    sessionToken?: string;
    authProvider?: string;
    hasAccount?: boolean;
    isNewAccount?: boolean;
    hasValidatedCode?: boolean;
  }>({});

  // Use our custom hook to measure content height
  const { ref: contentRef, height: contentHeight } = useMeasureHeight();

  // Define the flow order to determine direction
  const stepOrder: AuthStep[] = [
    'intro', 
    'invite-success', 
    'invite-code', 
    'signup', 
    'signup-success',
    'login', 
    'auth-success',
    'welcome-back'
  ];
  
  // Get animation direction based on step change
  const getDirection = (current: AuthStep, previous: AuthStep | null): number => {
    if (!previous) return 1; // Default to forward on first render
    
    const currentIndex = stepOrder.indexOf(current);
    const previousIndex = stepOrder.indexOf(previous);
    
    return currentIndex > previousIndex ? 1 : -1; // 1 for forward, -1 for backward
  };

  // Update step with direction tracking
  const goToStep = (step: AuthStep) => {
    console.log('Navigating to step:', step);
    setPreviousStep(currentStep);
    setCurrentStep(step);
  };

  // Updated determineAuthStep function with explicit screen values (no nulls)
/*   const determineAuthStep = (): { 
    step: AuthStep; 
    userData: Partial<typeof userData> | null; 
    action: (() => void) | null;
  } => {
    // Get all variables and convert to proper booleans
    const explicitlyLoggedOut = localStorage.getItem('tripperLoggedOut') === 'true';
    const hasAccount = localStorage.getItem('tripperHasAccount') === 'true';
    const userExists = localStorage.getItem('tripperUserExists') === 'true';
    const hasValidatedCode = localStorage.getItem('tripperCodeValidated') === 'true';
    const savedEmail = localStorage.getItem('tripperUserEmail');
    const inviteSent = localStorage.getItem('tripperInviteSent') === 'true';
    const sessionToken = localStorage.getItem('tripperSessionToken');
    const isAuthenticated = localStorage.getItem('tripperAuthenticated') === 'true';
    const userName = localStorage.getItem('tripperUserName');
    const userId = localStorage.getItem('tripperUserId');
    
    // Social auth checks
    const socialAuthSuccess = sessionStorage.getItem('socialAuthSuccess') === 'true';
    const socialAuthProvider = sessionStorage.getItem('socialAuthProvider');
    const socialAuthEmail = sessionStorage.getItem('socialAuthEmail');
    const accountCreated = sessionStorage.getItem('tripperAccountCreated') === 'true' || 
                          localStorage.getItem('tripperAccountJustCreated') === 'true';
    
    console.log('Auth state check:', { 
      explicitlyLoggedOut, hasAccount, userExists, hasValidatedCode, 
      inviteSent, savedEmail, sessionToken, isAuthenticated, 
      socialAuthSuccess, socialAuthProvider, socialAuthEmail, accountCreated, userName 
    });
  
    // Check for explicit logout first
    if (explicitlyLoggedOut) {
      console.log('User explicitly logged out, showing login');
      
      // Only clear the flag after a short delay
      setTimeout(() => {
        localStorage.removeItem('tripperLoggedOut');
      }, 500);
      
      // Always go to login when logged out
      return { 
        step: 'login' as AuthStep, 
        userData: {
          email: savedEmail || '',
          hasAccount: hasAccount || false
        }, 
        action: null 
      };
    }

    // 1. Already authenticated users
    if (isAuthenticated && hasAccount && savedEmail) {
      console.log('User is already authenticated, showing welcome-back then redirecting');
      const updatedUserData = {
        email: savedEmail,
        name: userName || '',
        hasAccount: true
      };
      return { 
        step: 'welcome-back' as AuthStep, 
        userData: updatedUserData, 
        action: () => {
          // This will be called after rendering welcome-back
          // We can either auto-redirect or let the user click a button
          setTimeout(() => onAuthSuccess(), 100); // Small timeout to ensure screen renders first
        }
      };
    }
    
    // 2. Users who just completed social auth
    if (socialAuthSuccess) {
      console.log('Social auth success detected, showing auth success screen');
      const updatedUserData = {
        email: socialAuthEmail,
        authProvider: socialAuthProvider,
        hasAccount: true
      };
      
      return { 
        step: 'auth-success' as AuthStep, 
        userData: updatedUserData,
        action: null
      };
    }
    
    // 3. Users who just created an account
    if (accountCreated) {
      console.log('Account created detection, showing signup success screen');
      const updatedUserData = {
        email: savedEmail || '',
        name: userName || '',
        isNewAccount: true
      };
      
      return { 
        step: 'signup-success' as AuthStep, 
        userData: updatedUserData,
        action: () => {
          sessionStorage.removeItem('tripperAccountCreated');
        }
      };
    }
    
    // 4. User has an account but is not authenticated
    if (hasAccount && hasValidatedCode && savedEmail && !isAuthenticated) {
      console.log('User has account but not authenticated, showing welcome back');
      const updatedUserData = {
        email: savedEmail,
        name: userName || '',
        hasAccount: true
      };
      return { 
        step: 'welcome-back' as AuthStep, 
        userData: updatedUserData, 
        action: null 
      };
    }
    
    // 5. OAuth in progress - show signup so they can see social buttons
    if (socialAuthProvider && !socialAuthSuccess) {
      console.log(`Social auth in progress for provider: ${socialAuthProvider}`);
      // We'll show signup because that's where social login buttons are
      return { 
        step: 'signup' as AuthStep, 
        userData: { 
          email: savedEmail,
          sessionToken: sessionToken || undefined
        }, 
        action: () => {
          // Immediately redirect to continue the OAuth flow
          if (socialAuthProvider) {
            const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
            window.location.href = `${API_BASE_URL}/api/auth/${socialAuthProvider}`;
          }
        }
      };
    }
    
    // 6. User has validated invite code but hasn't created account yet
    if (hasValidatedCode && savedEmail && !hasAccount) {
      console.log('User has validated code and needs to create an account, showing signup');
      const updatedUserData = {
        email: savedEmail,
        sessionToken: sessionToken || undefined
      };
      return { 
        step: 'signup' as AuthStep, 
        userData: updatedUserData, 
        action: null 
      };
    }
    
    // 7. User explicitly logged out but still has validated code
    if (explicitlyLoggedOut && hasValidatedCode) {
      console.log('User explicitly logged out, but has validated code, showing login');
      localStorage.removeItem('tripperLoggedOut');
      return { 
        step: 'login' as AuthStep, 
        userData: {
          email: savedEmail || '',
          hasAccount: hasAccount || false,
          sessionToken: sessionToken || undefined
        }, 
        action: null 
      };
    }
    
    // 8. User has received an invite code but hasn't validated it yet
    if (inviteSent && savedEmail && !hasValidatedCode) {
      console.log('User has invite but needs to enter code');
      const updatedUserData = {
        email: savedEmail,
        isWaitlisted: false
      };
      return { step: 'invite-code' as AuthStep, userData: updatedUserData, action: null };
    }
    
    // 9. User exists but hasn't validated code
    if (userExists && savedEmail && !hasValidatedCode) {
      console.log('User exists but has not validated code, showing code entry');
      const updatedUserData = {
        email: savedEmail
      };
      return { step: 'invite-code' as AuthStep, userData: updatedUserData, action: null };
    }
    
    // 10. User has requested an invite but hasn't entered code yet
    if (inviteSent && savedEmail) {
      console.log('User requested invite, showing invite success');
      const updatedUserData = {
        email: savedEmail,
        isWaitlisted: false
      };
      return { step: 'invite-success' as AuthStep, userData: updatedUserData, action: null };
    }

    // Default - New user
    console.log('New user or no state detected, showing intro');
    return { step: 'intro' as AuthStep, userData: null, action: null };
  }; */

  // Updated determineAuthStep function for AuthPanel.tsx
  // Updated determineAuthStep function with login failure handling
  const determineAuthStep = (): { 
    step: AuthStep; 
    userData: Partial<typeof userData> | null; 
    action: (() => void) | null;
  } => {
    // Get all variables and convert to proper booleans
    const explicitlyLoggedOut = localStorage.getItem('tripperLoggedOut') === 'true';
    const loginFailed = localStorage.getItem('tripperLoginFailed') === 'true';
    const hasAccount = localStorage.getItem('tripperHasAccount') === 'true';
    const userExists = localStorage.getItem('tripperUserExists') === 'true';
    const hasValidatedCode = localStorage.getItem('tripperCodeValidated') === 'true';
    const savedEmail = localStorage.getItem('tripperUserEmail');
    const inviteSent = localStorage.getItem('tripperInviteSent') === 'true';
    const sessionToken = localStorage.getItem('tripperSessionToken');
    const isAuthenticated = localStorage.getItem('tripperAuthenticated') === 'true';
    const userName = localStorage.getItem('tripperUserName');
    const userId = localStorage.getItem('tripperUserId');
    
    // Social auth checks
    const socialAuthSuccess = sessionStorage.getItem('socialAuthSuccess') === 'true';
    const socialAuthProvider = sessionStorage.getItem('socialAuthProvider');
    const socialAuthEmail = sessionStorage.getItem('socialAuthEmail');
    const accountCreated = sessionStorage.getItem('tripperAccountCreated') === 'true' || 
                          localStorage.getItem('tripperAccountJustCreated') === 'true';
    
    console.log("Determining auth step with context:", {
      explicitlyLoggedOut, 
      loginFailed,  // NEW: Check and log login failure flag
      hasAccount, 
      userExists, 
      hasValidatedCode, 
      savedEmail,
      isAuthenticated,
      lockToLoginScreen: lockToLoginScreen
    });

    if (userExists && hasValidatedCode) {
      goToStep('login');
    } else if (hasValidatedCode && !userExists) {
      // Code already validated but no account - go to signup
      localStorage.setItem('tripperCodeValidated', 'true');
      goToStep('signup');
    } else {
      // Normal flow - show success page
      goToStep('invite-success');
    }

    // NEW: Check for login failure or logout first - highest priority
    if (loginFailed || explicitlyLoggedOut || lockToLoginScreen) {
      console.log('Login failed or user logged out, showing login screen');
      
      // After handling the state, clear the login failed flag 
      // but NOT the logout flag (which times out)
      if (loginFailed) {
        setTimeout(() => {
          localStorage.removeItem('tripperLoginFailed');
        }, 500);
      }
      
      return { 
        step: 'login' as AuthStep, 
        userData: {
          email: savedEmail || '',
          hasAccount: hasAccount || false
        }, 
        action: null 
      };
    }

    // Clean state quick check - go straight to intro
    if (!explicitlyLoggedOut && !hasAccount && !userExists && !hasValidatedCode && 
      !inviteSent && !savedEmail && !sessionToken && !isAuthenticated && 
      !socialAuthSuccess && !userName) {
    console.log('Clean slate detected - showing intro screen');
    return { step: 'intro' as AuthStep, userData: null, action: null };
    }

    // NEW: If we're locked to login screen, always return login

    // Then update the lockToLoginScreen + explicitlyLoggedOut check:
    if (lockToLoginScreen || explicitlyLoggedOut) {
      console.log('User logged out or locked to login');
      
      // Check if this is a complete reset rather than a normal logout
      if (!hasAccount && !savedEmail) {
        console.log('Complete reset detected - showing intro screen');
        
        // Clear any logout flag
        setTimeout(() => {
          localStorage.removeItem('tripperLoggedOut');
        }, 100);
        
        return { step: 'intro' as AuthStep, userData: null, action: null };
      }
      
      console.log('Normal logout with account - showing login screen');
      // Only clear the flag after a short delay
      setTimeout(() => {
        localStorage.removeItem('tripperLoggedOut');
      }, 500);
      
      // Show login screen for normal logout
      return { 
        step: 'login' as AuthStep, 
        userData: {
          email: savedEmail || '',
          hasAccount: hasAccount || false
        }, 
        action: null 
      };
    }
    /* if (lockToLoginScreen || explicitlyLoggedOut) {
      console.log('User logged out or locked to login, showing login screen');
      
      // Schedule clearing of the flag (but this won't affect our UI thanks to the lock)
      if (explicitlyLoggedOut) {
        setTimeout(() => {
          localStorage.removeItem('tripperLoggedOut');
        }, 1000);
      }
      
      // Always go to login when logged out or locked
      return { 
        step: 'login' as AuthStep, 
        userData: {
          email: savedEmail || '',
          hasAccount: hasAccount || false
        }, 
        action: null 
      };
    } */

    /* // Check for explicit logout first
    if (explicitlyLoggedOut) {
      console.log('User explicitly logged out, showing login');
      
      // Only clear the flag after a short delay
      setTimeout(() => {
        localStorage.removeItem('tripperLoggedOut');
      }, 1000);
      
      // Always go to login when logged out
      return { 
        step: 'login' as AuthStep, 
        userData: {
          email: savedEmail || '',
          hasAccount: hasAccount || false
        }, 
        action: null 
      };
    } */

    // 1. Already authenticated users
    if (isAuthenticated && hasAccount && savedEmail) {
      console.log('User is already authenticated, showing welcome-back then redirecting');
      const updatedUserData = {
        email: savedEmail,
        name: userName || '',
        hasAccount: true
      };
      return { 
        step: 'welcome-back' as AuthStep, 
        userData: updatedUserData, 
        action: () => {
          // This will be called after rendering welcome-back
          // We can either auto-redirect or let the user click a button
          setTimeout(() => onAuthSuccess(), 100); // Small timeout to ensure screen renders first
        }
      };
    }
    
    // 2. Users who just completed social auth
    if (socialAuthSuccess) {
      console.log('Social auth success detected, showing auth success screen');
      const updatedUserData = {
        email: socialAuthEmail,
        authProvider: socialAuthProvider,
        hasAccount: true
      };
      
      return { 
        step: 'auth-success' as AuthStep, 
        userData: updatedUserData,
        action: null
      };
    }
    
    // 3. Users who just created an account
    if (accountCreated) {
      console.log('Account created detection, showing signup success screen');
      const updatedUserData = {
        email: savedEmail || '',
        name: userName || '',
        isNewAccount: true
      };
      
      return { 
        step: 'signup-success' as AuthStep, 
        userData: updatedUserData,
        action: () => {
          sessionStorage.removeItem('tripperAccountCreated');
        }
      };
    }
    
    // IMPORTANT CHANGE: Only show welcome-back for users with accounts who haven't explicitly logged out
    // and who are not authenticated. We've already checked explicitlyLoggedOut above.
    if (hasAccount && hasValidatedCode && savedEmail && !isAuthenticated && !explicitlyLoggedOut) {
      console.log('User has account but not authenticated, showing welcome back');
      const updatedUserData = {
        email: savedEmail,
        name: userName || '',
        hasAccount: true
      };
      return { 
        step: 'welcome-back' as AuthStep, 
        userData: updatedUserData, 
        action: null 
      };
    }
    
    // 5. OAuth in progress - show signup so they can see social buttons
    if (socialAuthProvider && !socialAuthSuccess) {
      console.log(`Social auth in progress for provider: ${socialAuthProvider}`);
      // We'll show signup because that's where social login buttons are
      return { 
        step: 'signup' as AuthStep, 
        userData: { 
          email: savedEmail,
          sessionToken: sessionToken || undefined
        }, 
        action: () => {
          // Immediately redirect to continue the OAuth flow
          if (socialAuthProvider) {
            const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
            window.location.href = `${API_BASE_URL}/api/auth/${socialAuthProvider}`;
          }
        }
      };
    }
    
    // 6. User has validated invite code but hasn't created account yet
    if (hasValidatedCode && savedEmail && !hasAccount) {
      console.log('User has validated code and needs to create an account, showing signup');
      const updatedUserData = {
        email: savedEmail,
        sessionToken: sessionToken || undefined
      };
      return { 
        step: 'signup' as AuthStep, 
        userData: updatedUserData, 
        action: null 
      };
    }
    
    // 7. User explicitly logged out but still has validated code
    if (explicitlyLoggedOut && hasValidatedCode) {
      console.log('User explicitly logged out, but has validated code, showing login');
      localStorage.removeItem('tripperLoggedOut');
      return { 
        step: 'login' as AuthStep, 
        userData: {
          email: savedEmail || '',
          hasAccount: hasAccount || false,
          sessionToken: sessionToken || undefined
        }, 
        action: null 
      };
    }

    // 8.0 // If user has an account but is not authenticated, show login
    if (hasAccount && savedEmail && !isAuthenticated && !explicitlyLoggedOut) {
      console.log('User has account, showing login');
      const updatedUserData = {
        email: savedEmail,
        name: userName || '',
        hasAccount: true
      };
      return { 
        step: 'login' as AuthStep, 
        userData: updatedUserData, 
        action: null 
      };
    }
    
    // 8.1 User has received an invite code but hasn't validated it yet
    if (inviteSent && savedEmail && !hasValidatedCode && !hasAccount) {
      console.log('User has invite but needs to enter code');
      const updatedUserData = {
        email: savedEmail,
        isWaitlisted: false
      };
      return { step: 'invite-code' as AuthStep, userData: updatedUserData, action: null };
    }
    
    // 9. User exists but hasn't validated code
    if (userExists && savedEmail && !hasValidatedCode && !hasAccount) {
      console.log('User exists but has not validated code, showing code entry');
      const updatedUserData = {
        email: savedEmail
      };
      return { step: 'invite-code' as AuthStep, userData: updatedUserData, action: null };
    }
    
    // 10. User has requested an invite but hasn't entered code yet
    if (inviteSent && savedEmail) {
      console.log('User requested invite, showing invite success');
      const updatedUserData = {
        email: savedEmail,
        isWaitlisted: false
      };
      return { step: 'invite-success' as AuthStep, userData: updatedUserData, action: null };
    }

    // Default - New user
    console.log('New user or no state detected, showing intro');
    return { step: 'intro' as AuthStep, userData: null, action: null };
  };

  // Handle logout
/*   const handleLogout = () => {
    // Get user email before logout to preserve it
    const savedEmail = localStorage.getItem('tripperUserEmail');
    
    // Use AuthService for logout
    authService.logout().then(() => {
      // Reset user data state but preserve email
      setUserData({
        email: savedEmail || '',
        hasAccount: true
      });
      
      // Force login screen
      goToStep('login');
    });
  }; */
/*   const handleLogout = () => {
    // Set lock to login screen before calling logout
    setLockToLoginScreen(true);
    
    // Get user email before logout to preserve it
    const savedEmail = localStorage.getItem('tripperUserEmail');
    
    // Use AuthService for logout
    authService.logout().then(() => {
      // Reset user data state but preserve email
      setUserData({
        email: savedEmail || '',
        hasAccount: true
      });
      
      // Force login screen
      goToStep('login');
      
      // Lock will automatically clear after 5 seconds (from the useEffect)
    });
  } */

  // Handle logout
  const handleLogout = async () => {
    // Set lock to login screen before calling logout
    setLockToLoginScreen(true);
    
    // Get user email before logout to preserve it
    const savedEmail = localStorage.getItem('tripperUserEmail');
    
    try {
      // Use AuthService for logout - prioritize server interaction
      console.log('Starting logout process via AuthService');
      const logoutResult = await authService.logout();
      
      // Log the result but proceed with logout UI changes regardless
      if (!logoutResult.success) {
        console.warn('Logout had issues:', logoutResult.message);
      }
      
      // Reset user data state but preserve email
      setUserData({
        email: savedEmail || '',
        hasAccount: true
      });
      
      // Force login screen
      goToStep('login');
    } catch (error) {
      console.error('Error during logout process:', error);
      // Still show login screen even if there was an error
      goToStep('login');
    }
  };    
    
    // Listen for auth state changes
/*   useEffect(() => {
    const handleAuthChange = () => {
      const { step, userData: newUserData, action } = determineAuthStep();
      
      // Update user data
      if (newUserData) {
        setUserData(prevData => ({...prevData, ...newUserData}));
      }
      
      // Execute any additional actions
      if (action) action();
      
      // Navigate to the determined step
      goToStep(step);
    };
    
    // Initial determination
    handleAuthChange();
    
    // Custom event for logout - make this the priority listener
    const handleLogoutEvent = () => {
      console.log('Received logout event, forcing login screen');
      
      // Preserve user email for login form
      const savedEmail = localStorage.getItem('tripperUserEmail');
      
      // Update userData for login form
      setUserData(prevData => ({
        ...prevData,
        email: savedEmail || '',
        hasAccount: true
      }));
      
      // Don't clear tripperLoggedOut flag immediately
      goToStep('login');
    };
    
    window.addEventListener('tripperLogout', handleLogoutEvent);
    
    // Storage changes
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key && ['tripperAuthenticated', 'tripperLoggedOut', 'tripperHasAccount'].includes(event.key)) {
        handleAuthChange();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('tripperLogout', handleLogoutEvent);
    };
  }, []); */

  // src/components/Auth/AuthPanel.tsx
// Only including the changes needed for OAuth handling

  useEffect(() => {
    const handleAuthChange = () => {
      const { step, userData: newUserData, action } = determineAuthStep();
      
      // Update user data
      if (newUserData) {
        setUserData(prevData => ({...prevData, ...newUserData}));
      }
      
      // Execute any additional actions
      if (action) action();
      
      // Navigate to the determined step
      goToStep(step);
    };
    
    // Initial determination
    handleAuthChange();
    
    // Custom event for logout
    /* const handleLogoutEvent = () => {
      console.log('Received logout event, forcing login screen');
      
      // Preserve user email for login form
      const savedEmail = localStorage.getItem('tripperUserEmail');
      
      // Update userData for login form
      setUserData(prevData => ({
        ...prevData,
        email: savedEmail || '',
        hasAccount: true
      }));
      
      goToStep('login');
    }; */
    const handleLogoutEvent = () => {
      // Check if this is a complete reset or just a regular logout
      const hasAccount = localStorage.getItem('tripperHasAccount') === 'true';
      const hasEmail = !!localStorage.getItem('tripperUserEmail');
      
      if (hasAccount && hasEmail) {
        // This is a normal logout - lock to login screen
        console.log('Normal logout detected - locking to login screen');
        setLockToLoginScreen(true);
        
        setTimeout(() => {
          console.log('Unlocking login screen lock');
          setLockToLoginScreen(false);
        }, 5000);
      } else {
        // This is a complete reset - DO NOT lock to login screen
        console.log('Complete reset detected - not locking to any screen');
        setLockToLoginScreen(false);
        
        // Clear the logout flag immediately in this case
        localStorage.removeItem('tripperLoggedOut');
      }
    };
    
    window.addEventListener('tripperLogout', handleLogoutEvent);
    // New: custom event for successful OAuth authentication
    const handleAuthSuccessEvent = () => {
      console.log('Received auth success event');
      
      // Call onAuthSuccess to update the parent component
      if (onAuthSuccess) {
        onAuthSuccess();
      }
    };

    /* const handleLogoutLock = () => {
      console.log('Locking to login screen after logout');
      setLockToLoginScreen(true);
      
      // Keep locked to login screen for 5 seconds
      setTimeout(() => {
        console.log('Unlocking login screen lock');
        setLockToLoginScreen(false);
      }, 5000);
    };

    window.addEventListener('tripperLogout', handleLogoutLock); */


    // Check if we've recently logged out (page refresh case)
   /*  if (localStorage.getItem('tripperLoggedOut') === 'true') {
      handleLogoutLock();
    } */

    window.addEventListener('tripperLogout', handleLogoutEvent);
    window.addEventListener('tripperAuthenticated', handleAuthSuccessEvent);
    
    // Storage changes
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key && ['tripperAuthenticated', 'tripperLoggedOut', 'tripperHasAccount'].includes(event.key)) {
        handleAuthChange();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      //window.removeEventListener('tripperLogout', handleLogoutLock);
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('tripperLogout', handleLogoutEvent);
      window.removeEventListener('tripperAuthenticated', handleAuthSuccessEvent);
    };
  }, [onAuthSuccess]); // Add onAuthSuccess to dependencies

  useEffect(() => {
    const handleLogoutEvent = () => {
      // Check if this is a complete reset or just a regular logout
      const hasAccount = localStorage.getItem('tripperHasAccount') === 'true';
      const hasEmail = !!localStorage.getItem('tripperUserEmail');
      
      if (hasAccount && hasEmail) {
        // This is a normal logout - lock to login screen
        console.log('Normal logout detected - locking to login screen');
        setLockToLoginScreen(true);
        
        setTimeout(() => {
          console.log('Unlocking login screen lock');
          setLockToLoginScreen(false);
        }, 5000);
      } else {
        // This is a complete reset - DO NOT lock to login screen
        console.log('Complete reset detected - not locking to any screen');
        setLockToLoginScreen(false);
        
        // Clear the logout flag immediately in this case
        localStorage.removeItem('tripperLoggedOut');
      }
    };
    
    window.addEventListener('tripperLogout', handleLogoutEvent);
    
    // Check if we've recently logged out (page refresh case)
    if (localStorage.getItem('tripperLoggedOut') === 'true') {
      // Check if it's a clean state or regular logout
      const hasAccount = localStorage.getItem('tripperHasAccount') === 'true';
      const hasEmail = !!localStorage.getItem('tripperUserEmail');
      
      if (!hasAccount || !hasEmail) {
        // It's a clean state - clear the flag
        console.log('Clean state detected - clearing logout flag');
        localStorage.removeItem('tripperLoggedOut');
      } else {
        // Regular logout - normal lock behavior
        console.log('Previous logout detected - locking to login screen');
        setLockToLoginScreen(true);
        
        setTimeout(() => {
          console.log('Unlocking login screen lock');
          setLockToLoginScreen(false);
        }, 5000);
      }
    }
    
    return () => {
      window.removeEventListener('tripperLogout', handleLogoutEvent);
    };
  }, []);

  // Handle invite request success
  const handleInviteRequestSuccess = (data: { 
    email: string; 
    name: string; 
    isWaitlisted: boolean;
    userExists?: boolean;
    hasValidatedCode?: boolean;
  }) => {
    setUserData({
      email: data.email,
      name: data.name,
      isWaitlisted: data.isWaitlisted,
      hasValidatedCode: data.hasValidatedCode
    });
    
    localStorage.setItem('tripperUserEmail', data.email);
    localStorage.setItem('tripperInviteSent', 'true');

    if (data.userExists && data.hasValidatedCode) {
      goToStep('login');
    } else {
      console.log('Normal flow - show success page');
      goToStep('invite-success');
    }
  };
  
  // Handler for when going from success page back to code entry
  const handleGoToInviteCode = () => {
    goToStep('invite-code');
  };

  // Handler for when the invite code validation is successful
  const handleInviteSuccess = (token: string) => {
    setUserData(prevData => ({
      ...prevData,
      sessionToken: token
    }));
    
    localStorage.setItem('tripperSessionToken', token);
    localStorage.setItem('tripperCodeValidated', 'true');
    
    goToStep('signup');
  };
  
  // Handler for successful signup
  const handleSignupSuccess = (hasCreatedAccount: boolean) => {
    if (hasCreatedAccount) {
      console.log('New account created, showing signup success screen');
      sessionStorage.setItem('tripperAccountCreated', 'true');
      localStorage.setItem('tripperAccountJustCreated', 'true');
      
      if (userData.name) {
        localStorage.setItem('tripperUserName', userData.name);
      }
      
      localStorage.setItem('tripperHasAccount', 'true');
      setUserData(prevData => ({
        ...prevData,
        hasAccount: true,
        isNewAccount: true
      }));
      
      goToStep('signup-success');
    } else {
      goToStep('login');
    }
  };

/*   // Handler for successful login
  const handleLoginSuccess = (provider?: string) => {
    localStorage.setItem('tripperHasAccount', 'true');
    
    if (provider) {
      setUserData(prevData => ({
        ...prevData,
        authProvider: provider,
        hasAccount: true
      }));
      
      goToStep('auth-success');
    } else {
      finishAuthentication();
    }
  }; */

  // Handler for successful login
  const handleLoginSuccess = async (provider?: string) => {
    // Ensure we record that user has an account
    localStorage.setItem('tripperHasAccount', 'true');
    
    if (provider) {
      // Social provider login flow
      setUserData(prevData => ({
        ...prevData,
        authProvider: provider,
        hasAccount: true
      }));
      
      // Show auth success screen for social logins
      goToStep('auth-success');
    } else {
      // Skip any intermediate screens and go directly to app
      console.log('Login successful, proceeding with authentication');
      await finishAuthentication();
    }
  };

  // Handler for welcome back actions
  const handleWelcomeBackAction = (action: 'login' | 'logout') => {
    if (action === 'login') {
      goToStep('login');
    } else {
      handleLogout();
    }
  };

  // Final success handler
/*   const finishAuthentication = () => {
    localStorage.removeItem('tripperSessionToken');
    localStorage.setItem('tripperAuthenticated', 'true');
    localStorage.setItem('tripperHasAccount', 'true');
    
    // If email exists in userData but not in localStorage, set it
    if (userData.email && !localStorage.getItem('tripperUserEmail')) {
      localStorage.setItem('tripperUserEmail', userData.email);
    }
    
    // If name exists in userData but not in localStorage, set it
    if (userData.name && !localStorage.getItem('tripperUserName')) {
      localStorage.setItem('tripperUserName', userData.name);
    }
    
    onAuthSuccess();
  }; */

  // Final success handler with proper server verification
  const finishAuthentication = async () => {
    try {
      // Verify authentication with server before proceeding
      const isAuthValid = await authService.checkAuth();
      
      if (!isAuthValid) {
        console.error('Server rejected authentication during finishAuthentication');
        // If server rejected auth, show login again
        goToStep('login');
        return;
      }
      
      // Server confirmed authentication, proceed with app access
      console.log('Authentication confirmed by server');
      
      // Clean up any temporary tokens
      localStorage.removeItem('tripperSessionToken');
      localStorage.removeItem('tripperLoggedOut');
      localStorage.setItem('tripperAuthenticated', 'true');
      localStorage.setItem('tripperHasAccount', 'true');
      
      // Ensure email and name persistence
      if (userData.email && !localStorage.getItem('tripperUserEmail')) {
        localStorage.setItem('tripperUserEmail', userData.email);
      }
      
      if (userData.name && !localStorage.getItem('tripperUserName')) {
        localStorage.setItem('tripperUserName', userData.name);
      }
      
      // Notify parent component of success
      onAuthSuccess();
    } catch (error) {
      console.error('Error during authentication finalization:', error);
      // On error, stay on current screen
    }
  };
  // Calculate the animation direction
  const direction = getDirection(currentStep, previousStep);

  // Render the appropriate component based on the current step
  const renderStep = (step: AuthStep) => {
    console.log("Rendering step:", step, "with userData:", userData);
    
    switch (step) {
      case 'intro':
        return (
          <IntroPage 
            onRequestSuccess={handleInviteRequestSuccess} 
          />
        );
      case 'invite-success':
        return (
          <RequestSuccessPage 
            email={userData.email}
            isWaitlisted={userData.isWaitlisted}
            onBackToLogin={handleGoToInviteCode}
          />
        );
      case 'invite-code':
        return (
          <InviteModal 
            email={userData.email}
            onSuccess={handleInviteSuccess}
            onBack={() => goToStep('intro')}
          />
        );
      case 'signup':
        return (
          <SignupModal 
            email={userData.email}
            sessionToken={userData.sessionToken}
            onSuccess={handleSignupSuccess}
            onBack={() => goToStep('invite-code')}
            onSignIn={() => goToStep('login')} // Add this line
          />
        );
      case 'signup-success':
        return (
          <SignupSuccessScreen
          email={userData.email}
          name={userData.name}
          onContinue={() => goToStep('login')} // Change from finishAuthentication
          autoRedirectDelay={5000} // 5 seconds
        />
        );
      case 'login':
        // Make sure we have userData with email for the login modal
        if (!userData.email) {
          const savedEmail = localStorage.getItem('tripperUserEmail');
          if (savedEmail) {
            setUserData(prev => ({ ...prev, email: savedEmail }));
          }
        }
        
        return (
          <LoginModal 
            sessionToken={userData.sessionToken}
            userEmail={userData.email}
            onLoginSuccess={handleLoginSuccess}
            onBack={() => userData.hasAccount ? goToStep('welcome-back') : goToStep('signup')}
            onSignUp={() => goToStep('signup')} // Add this line
          />
        );
      case 'auth-success':
        return (
          <AuthSuccessScreen
            providerName={userData.authProvider}
            email={userData.email}
            userName={userData.name}
            onContinue={finishAuthentication}
            autoRedirectDelay={3001}
          />
        );
      case 'welcome-back':
        return (
          <WelcomeBackScreen
            email={userData.email}
            name={userData.name}
            onLogin={() => handleWelcomeBackAction('login')}
            onLogout={() => handleWelcomeBackAction('logout')}
            onContinue={finishAuthentication} // Use finishAuthentication to complete the auth flow
          />
        );
      default:
        return <div>Unknown step: {step}</div>;
    }
  };

  // Get direct authentication state from localStorage for consistency
  const isUserAuthenticated = localStorage.getItem('tripperAuthenticated') === 'true';
  const isLoggedOut = localStorage.getItem('tripperLoggedOut') === 'true';
  const shouldShowPanel = !isUserAuthenticated || isLoggedOut;

  return (
    <AnimatePresence mode="wait">
      {shouldShowPanel && (
        <motion.div 
          initial="hidden"
          animate="visible"
          exit="exit"
          variants={panelVariants}
          className="fixed inset-y-0 right-0 z-[100] bg-white shadow-xl flex flex-col"
          style={{ 
            width: 'var(--auth-panel-width)',
            top: '0'
          }}
        >
          {/* Fixed container for the auth panel */}
          <div className="relative flex-grow flex flex-col overflow-hidden">
            {/* Content slides within this fixed container */}
            <div className="flex-grow overflow-hidden">
              <AnimatePresence mode="wait" initial={false}>
                <motion.div
                  key={currentStep}
                  custom={direction}
                  variants={slideVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  ref={contentRef}
                  className="h-full flex flex-col"
                >
                  {renderStep(currentStep)}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}